import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ReplayMessage {
  id: string;
  whatsapp_message_id: string | null;
  group_id: string;
  org_id: string;
  sender_name: string | null;
  sender_phone: string | null;
  content: string | null;
  message_type: string | null;
  created_at: string;
  whatsapp_timestamp: string | null;
}

export interface MessageTrace {
  message: ReplayMessage;
  intents: Array<{
    id: string;
    intent: string;
    confidence: number;
    was_correct: boolean | null;
    corrected_intent: string | null;
    feedback_source: string | null;
  }>;
  tom: Array<{
    id: string;
    primary_intent: string | null;
    secondary_intents: string[] | null;
    turn_affect: unknown;
    urgency: number | null;
    vulnerability: number | null;
    openness_to_challenge: number | null;
    confidence_parsing: number | null;
    raw_signals: unknown;
  }>;
  metacog: Array<{
    id: string;
    task_class: string | null;
    pre_response_confidence: number | null;
    decision: string;
    applied: boolean;
    reason: string | null;
    signals: unknown;
    thresholds: unknown;
  }>;
  modelDecisions: Array<{
    id: string;
    task_class: string | null;
    provider_used: string | null;
    model_used: string | null;
    skill_name: string | null;
    success: boolean | null;
    was_fallback: boolean | null;
    verifier_rejected: boolean | null;
    input_tokens: number | null;
    output_tokens: number | null;
    latency_ms: number | null;
    estimated_cost_usd: number | null;
    error_type: string | null;
    reason: string | null;
    alternatives_considered: unknown;
    classifier_version: string | null;
    created_at: string;
  }>;
  contextSnapshots: Array<{
    id: string;
    decision_id: string | null;
    sources_included: string[] | null;
    sources_skipped: string[] | null;
    total_tokens_used: number | null;
    context_window_pct: number | null;
    context_sources: unknown;
  }>;
  skillsLegacy: Array<{
    id: string;
    skill_name: string | null;
    metacog_decision: string | null;
    rollout_state_at_exec: string | null;
    match_score: number | null;
    self_report_score: number | null;
    execution_time_ms: number | null;
    output_length: number | null;
    was_sent_to_group: boolean | null;
    is_test: boolean | null;
    pre_response_confidence: number | null;
    task_class: string | null;
    context_metadata: unknown;
  }>;
  skillsV19: Array<{
    id: string;
    skill_slug: string | null;
    skill_version: number | null;
    status: string;
    matched_intent: string | null;
    match_score: number | null;
    pre_check: unknown;
    post_check: unknown;
    metacog_decision: unknown;
    tools_used: string[] | null;
    input_tokens: number | null;
    output_tokens: number | null;
    latency_ms: number | null;
    cost_usd: number | null;
    shadow_mode: boolean | null;
    error_message: string | null;
    user_reaction: string | null;
  }>;
  persona: {
    id: string;
    persona_base: unknown;
    persona_base_version: number | null;
    cultural_register: string | null;
    enabled_skills: string[] | null;
    source: string | null;
    notes: string | null;
    created_at: string;
  } | null;
  affect: {
    valence: number;
    arousal: number;
    dominance: number;
    signals: unknown;
    window_start: string;
    window_end: string;
  } | null;
  workingMemory: unknown;
}

export function useReplayGroups() {
  return useQuery({
    queryKey: ["replay_groups"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("syra_groups")
        .select("id, name, group_jid, sandbox_mode, active")
        .order("name");
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useReplayMessages(params: {
  group_id: string | null;
  mode: "window" | "list";
  from?: string;
  to?: string;
  limit?: number;
}) {
  const { group_id, mode, from, to, limit = 100 } = params;
  return useQuery({
    queryKey: ["replay_messages", group_id, mode, from, to, limit],
    enabled: !!group_id,
    queryFn: async () => {
      if (!group_id) return [];
      let q = supabase
        .from("syra_messages")
        .select(
          "id, whatsapp_message_id, group_id, org_id, sender_name, sender_phone, content, message_type, created_at, whatsapp_timestamp"
        )
        .eq("group_id", group_id)
        .order("created_at", { ascending: false })
        .limit(limit);
      if (mode === "window") {
        if (from) q = q.gte("created_at", from);
        if (to) q = q.lte("created_at", to);
      }
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as ReplayMessage[];
    },
  });
}

export function useMessageTraces(messages: ReplayMessage[]) {
  const ids = messages.map((m) => m.id);
  const waIds = messages.map((m) => m.whatsapp_message_id).filter(Boolean) as string[];
  const groupId = messages[0]?.group_id ?? null;
  const orgId = messages[0]?.org_id ?? null;

  return useQuery({
    queryKey: ["message_traces", ids.join(","), groupId],
    enabled: ids.length > 0 && !!groupId,
    queryFn: async (): Promise<MessageTrace[]> => {
      if (!groupId || !orgId || ids.length === 0) return [];

      // Fetch all parallel
      const [
        intentsRes,
        tomRes,
        metacogRes,
        modelDecRes,
        skillsLegacyRes,
        skillsV19Res,
        personaRes,
        affectRes,
        wmRes,
      ] = await Promise.all([
        supabase.from("intent_events").select("*").in("message_id", ids),
        supabase.from("tom_turn_inferences").select("*").in("message_id", waIds.length ? waIds : ids),
        supabase
          .from("metacog_runtime_decisions")
          .select("*")
          .in("message_id", waIds.length ? waIds : ids),
        // model_decisions don't have message_id; correlate by group + window
        supabase
          .from("model_decisions")
          .select("*")
          .eq("group_id", groupId)
          .gte("created_at", messages[messages.length - 1].created_at)
          .lte(
            "created_at",
            new Date(new Date(messages[0].created_at).getTime() + 60_000).toISOString()
          ),
        supabase
          .from("skill_executions")
          .select("*")
          .eq("group_id", groupId)
          .gte("created_at", messages[messages.length - 1].created_at)
          .lte(
            "created_at",
            new Date(new Date(messages[0].created_at).getTime() + 60_000).toISOString()
          ),
        supabase
          .from("syra_skill_executions_v19")
          .select("*")
          .in("message_id", ids),
        supabase
          .from("syra_agent_persona_history")
          .select("*")
          .eq("org_id", orgId)
          .lte("created_at", messages[0].created_at)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from("affect_states")
          .select("*")
          .eq("group_id", groupId)
          .lte("window_start", messages[0].created_at)
          .gte("window_end", messages[messages.length - 1].created_at)
          .order("window_end", { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from("working_memory_state")
          .select("*")
          .eq("group_id", groupId)
          .order("updated_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);

      const intents = intentsRes.data ?? [];
      const tom = tomRes.data ?? [];
      const metacog = metacogRes.data ?? [];
      const modelDec = modelDecRes.data ?? [];
      const skillsLegacy = skillsLegacyRes.data ?? [];
      const skillsV19 = skillsV19Res.data ?? [];
      const decisionIds = modelDec.map((d: any) => d.id);
      const ctxRes =
        decisionIds.length > 0
          ? await supabase.from("context_snapshots").select("*").in("decision_id", decisionIds)
          : { data: [] };
      const ctx = ctxRes.data ?? [];

      return messages.map((m) => {
        const winStart = new Date(m.created_at).getTime();
        const winEnd = winStart + 60_000;
        const dec = modelDec.filter((d: any) => {
          const t = new Date(d.created_at).getTime();
          return t >= winStart && t <= winEnd;
        });
        const decIds = dec.map((d: any) => d.id);
        const sk = skillsLegacy.filter((s: any) => {
          const t = new Date(s.created_at).getTime();
          return t >= winStart && t <= winEnd;
        });
        return {
          message: m,
          intents: intents.filter((i: any) => i.message_id === m.id) as any,
          tom: tom.filter(
            (t: any) => t.message_id === m.whatsapp_message_id || t.message_id === m.id
          ) as any,
          metacog: metacog.filter(
            (mc: any) => mc.message_id === m.whatsapp_message_id || mc.message_id === m.id
          ) as any,
          modelDecisions: dec as any,
          contextSnapshots: ctx.filter((c: any) => decIds.includes(c.decision_id)) as any,
          skillsLegacy: sk as any,
          skillsV19: skillsV19.filter((s: any) => s.message_id === m.id) as any,
          persona: (personaRes.data as any) ?? null,
          affect: (affectRes.data as any) ?? null,
          workingMemory: (wmRes.data as any) ?? null,
        };
      });
    },
  });
}
